// Glue from the sim to AudioState, so main.ts needs only a couple of lines.

import type { Car } from '../sim/car';
import type { Session } from '../sim/session';
import type { AudioState } from './carAudio';

/**
 * MGU-K power from the energy store's change of charge. The power unit applies
 * exactly `charge -= mguK·dt`, so this recovers the deploy (+) / harvest (−)
 * power without the car having to expose its last PowerOutput. Averages over
 * all sim steps in a frame. Charge jumps (session reset) are ignored.
 */
export class MguMeter {
  private last: number | undefined;
  kw = 0;

  update(charge: number, frameDt: number): number {
    if (this.last !== undefined && frameDt > 0) {
      const kw = (this.last - charge) / frameDt / 1000;
      this.kw = Math.abs(kw) <= 400 ? kw : this.kw;
    }
    this.last = charge;
    return this.kw;
  }
}

/**
 * Fill `out` (reused every frame) from the session.
 * @param throttle pedal 0..1 as sent to the sim this frame
 * @param mguKW MGU-K power in kW (+ deploy, − harvest), e.g. from MguMeter
 * @param impact largest session.impact seen over this frame's sim steps, m/s
 */
export function fillAudioState(out: AudioState, session: Session, throttle: number, mguKW: number, impact: number): AudioState {
  const car: Car = session.car;
  out.rpm = car.gearbox.rpm;
  out.gear = car.gearbox.gear;
  out.sinceShift = car.gearbox.sinceShift;
  out.throttle = throttle;
  out.speed = car.speed;
  out.mguKW = mguKW;
  out.gripFront = car.gripUseFront;
  out.gripRear = car.gripUseRear;
  out.slipFront = car.slipFront;
  out.slipRear = car.slipRear;
  out.surface = session.surface;
  out.impact = impact;
  return out;
}
